/**
 * World-state hashing for the twin-run determinism test.
 *
 * Two 32-bit FNV-flavoured accumulators with different mixing constants, printed as
 * 16 hex chars. Not cryptographic — it only has to catch drift, and it has to be
 * allocation-free enough to run every 600 ticks without perturbing the perf proxy.
 *
 * Floats are hashed by their exact IEEE-754 bits. We do NOT quantize: WASM arithmetic
 * is bit-deterministic by spec (no x87 excess precision, no FMA contraction in the
 * non-SIMD Rapier build), so a same-build twin run must match to the last bit. If it
 * ever doesn't, that is a real bug and the test should say so loudly.
 */

const buf = new ArrayBuffer(8);
const dv = new DataView(buf);

export class Hasher {
  private h1 = 0x811c9dc5;
  private h2 = 0xcbf29ce4;

  u32(v: number): this {
    const x = v >>> 0;
    this.h1 = (Math.imul(this.h1 ^ x, 0x01000193) >>> 0) ^ (this.h2 >>> 7);
    this.h2 = (Math.imul(this.h2 ^ (x + 0x9e3779b9), 0x85ebca6b) >>> 0) ^ (this.h1 >>> 13);
    this.h1 = this.h1 >>> 0;
    this.h2 = this.h2 >>> 0;
    return this;
  }

  i32(v: number): this {
    return this.u32(v | 0);
  }

  /** Exact bits of a double. */
  f64(v: number): this {
    dv.setFloat64(0, v, true);
    return this.u32(dv.getUint32(0, true)).u32(dv.getUint32(4, true));
  }

  bool(v: boolean): this {
    return this.u32(v ? 0x9e3779b9 : 0x7f4a7c15);
  }

  str(s: string): this {
    this.u32(s.length);
    for (let i = 0; i < s.length; i++) this.u32(s.charCodeAt(i));
    return this;
  }

  hex(): string {
    const a = (this.h1 >>> 0).toString(16).padStart(8, '0');
    const b = (this.h2 >>> 0).toString(16).padStart(8, '0');
    return a + b;
  }
}

export function hashOf(fn: (h: Hasher) => void): string {
  const h = new Hasher();
  fn(h);
  return h.hex();
}
